/**
 * Home on the console: the workbench's Home — the week ahead, one quiet row of numbers and the feed — for the
 * signed-in person across their organisation (`GET /home/v1`, packages/shared/src/schemas/homeRoute.ts). The
 * platform builds the cards with the workbench's own models; this lays them out as the workbench's HomeHq does,
 * draws the strip again in the person's own time zone from the answer's events, orders the feed by what the
 * person taught it (useful, not useful, pin, hide; kept per person by the platform), and sends every press to the
 * console's own page for the thing.
 */
import './homeHq.css';
import './homeConsole.css';

import type { HomeResponse } from '@aioe/shared';
import { dateLong, time } from '@aioe/shared/wording/dateTime';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { routes } from '../../api/client.ts';
import { useLoad } from '../../hooks.ts';
import { buildFeed, capFeed, counterWords, dayFilter, type FeedItem, type FeedPrefs, selectedDay, weekAhead } from '../../sharedHome.ts';
import { ErrorNote } from '../ui.tsx';
import { getFeedPrefs, onFeedbackAnswered, takeFeedPrefs, useFeedPrefs } from './feedPrefs.ts';
import { HomeDayStrip } from './HomeDayStrip.tsx';
import { HomeFeed } from './HomeFeed.tsx';
import { HomeStats } from './HomeStats.tsx';

/** Home inside the console's page: as wide as the page, which pads it already. */
const FULL_WIDTH = { width: '100%', paddingLeft: 0, paddingRight: 0, paddingTop: 0 } as const;

/** The clock, ticking once a minute on the minute. */
export function useMinute(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    let timer: number | undefined;
    const tick = () => {
      setNow(Date.now());
      timer = window.setTimeout(tick, 60_000 - (Date.now() % 60_000) + 50);
    };
    timer = window.setTimeout(tick, 60_000 - (Date.now() % 60_000) + 50);
    return () => window.clearTimeout(timer);
  }, []);
  return now;
}

// The person's clock and date order (Settings › You › Date and time), never a fixed one.
export const clockWords = (now: number): string => time(now);
export const dateWords = (now: number): string => dateLong(now, { weekday: true, year: false });

/** The header's clock, as the workbench's Home has it. */
export function HomeClock() {
  const now = useMinute();
  return (
    <div className="home-clock" aria-label={`${clockWords(now)}, ${dateWords(now)}`}>
      <span className="home-clock__time">{clockWords(now)}</span>
      <span className="home-clock__date">{dateWords(now)}</span>
    </div>
  );
}

/** Home's answer, read again every minute (an unchanged one is a 304) and after every press the platform answers. */
function useHome() {
  const home = useLoad(() => routes.home(), [], 60_000);
  const [problem, setProblem] = useState<string>();
  useEffect(() => {
    if (home.data) takeFeedPrefs(home.data.prefs);
  }, [home.data]);
  const { reload } = home;
  useEffect(
    () =>
      onFeedbackAnswered((error) => {
        setProblem(error);
        reload();
      }),
    [reload],
  );
  return { ...home, problem };
}

export function HomeView() {
  const navigate = useNavigate();
  const now = useMinute();
  const home = useHome();
  const data: HomeResponse | undefined = home.data;
  const prefs = useFeedPrefs();
  // The strip in the person's own time zone, from the events the platform drew its own from.
  const cells = useMemo(() => weekAhead(data?.events ?? [], now), [data?.events, now]);
  const [pressed, setPressed] = useState<number | undefined>(undefined);
  const day = selectedDay(cells, pressed);
  // What a person taught (useful / not useful) is read when Home opens and when it is refreshed: the card they just
  // pressed does not slide away under their pointer. Pins and hides apply at once.
  const [weightsAtRefresh, setWeightsAtOpen] = useState<FeedPrefs['weights'] | undefined>(undefined);
  const weightsAtOpen = useMemo(() => weightsAtRefresh ?? (data?.prefs.weights as FeedPrefs['weights'] | undefined) ?? {}, [weightsAtRefresh, data?.prefs.weights]);
  const items = useMemo(() => (data?.feed ?? []) as unknown as FeedItem[], [data?.feed]);
  const feed = useMemo(() => buildFeed(items, { ...prefs, weights: weightsAtOpen }, now, dayFilter(cells, pressed)), [items, prefs, weightsAtOpen, now, cells, pressed]);
  const { shown, all } = useMemo(() => capFeed(feed), [feed]);
  const [position, setPosition] = useState({ index: 0, total: 0 });
  const onPosition = useCallback((index: number, total: number) => setPosition((p) => (p.index === index && p.total === total ? p : { index, total })), []);

  const go = useCallback((view: string) => navigate(view.startsWith('/') ? view : '/'), [navigate]);
  const open = useCallback((item: FeedItem) => go(item.open.view), [go]);
  const { reload } = home;
  const refresh = useCallback(() => {
    setWeightsAtOpen(getFeedPrefs().weights);
    reload();
  }, [reload]);

  if (!data) return home.error ? <ErrorNote error={home.error} /> : <div className="min-h-[24rem]" aria-busy="true" />;
  // No Team chip: on the console it would say only how many are running, which the Running number already says.
  return (
    // The page already pads and the header is the page's: Home takes the page's full width and none of its own padding.
    <div className="home-hq" style={FULL_WIDTH} data-home-hq data-feed="on">
      <HomeDayStrip cells={cells} selected={day.start} onSelect={setPressed} needsYou={data.chips.needsYou} onOpenInbox={() => navigate('/inbox')} />
      <HomeStats stats={data.stats.map((s) => ({ ...s, value: s.value }))} onOpen={go} />
      <div className="home-stage" key={day.start}>
        <div className="home-stage__head">
          <span className="home-stage__kicker">Feed</span>
          <span className="home-stage__count tabular-nums" data-home-counter>
            {counterWords(position.index, shown.length, all)}
          </span>
        </div>
        <HomeFeed
          items={shown}
          prefs={prefs}
          dayKey={day.start}
          emptyTitle={day.isToday ? 'Nothing needs you yet' : 'Nothing for that day'}
          emptyText={day.isToday ? 'Approvals, questions, plans waiting for you and finished work across your projects show up here, one card at a time.' : `${day.summary} Anything that needs you stays on every day.`}
          onOpen={open}
          onOpenView={go}
          onRefresh={refresh}
          onPosition={onPosition}
          emptyAction={{ label: 'Open the Inbox', onPress: () => navigate('/inbox') }}
        />
      </div>
      <ErrorNote error={home.problem} />
    </div>
  );
}
